import {type FormEvent, useState} from "react";
import {BaseAlert, BaseBadge, BaseBreadcrumb, BaseButton, BaseCard, BaseInput} from "@/components";
import {contactSearchSchema} from "./contactSearch.schema";
import type {Contact} from "./contacts.types";
import {useAddContact, useContacts, useDeleteContact, useSearchContactByEmail} from "./useContacts";

const ContactsPage = () => {
  const {contacts, isLoading, isError, error, refetch} = useContacts();
  const searchMutation = useSearchContactByEmail();
  const addMutation = useAddContact();
  const deleteMutation = useDeleteContact();

  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState<string | undefined>();
  const [foundContact, setFoundContact] = useState<Contact | null>(null);

  const handleSearch = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const result = contactSearchSchema.safeParse({email});
    if (!result.success) {
      setEmailError(result.error.issues[0]?.message ?? "Ingresa un email válido");
      return;
    }
    setEmailError(undefined);
    setFoundContact(null);
    searchMutation.mutate(result.data.email, {
      onSuccess: result => {
        if (!result) {
          setEmailError("No encontramos un usuario con ese email");
        } else {
          setFoundContact(result);
        }
      },
    });
  };

  const handleAdd = () => {
    if (!foundContact) return;
    addMutation.mutate(foundContact.contactId || foundContact.id, {
      onSuccess: () => {
        setFoundContact(null);
        setEmail("");
      },
    });
  };

  const handleDelete = (contact: Contact) => {
    if (!window.confirm(`¿Eliminar a ${contact.fullName} de tus contactos?`)) return;
    deleteMutation.mutate(contact.id);
  };

  const serverError = searchMutation.error ?? addMutation.error ?? deleteMutation.error;

  return (
    <section className="mx-auto flex w-full max-w-2xl flex-col gap-6 px-4 py-8">
      <BaseBreadcrumb items={[{label: "Contactos"}]} />
      <header className="flex flex-col gap-2">
        <h1 className="text-[var(--font-h1)] font-bold text-[var(--color-text)]">Mis contactos</h1>
        <p className="text-[var(--font-body-lg)] text-[var(--color-text-secondary)]">
          Administra los contactos con los que transfieres saldo.
        </p>
      </header>

      {serverError ? (
        <BaseAlert
          variant="danger"
          title="Ocurrió un error"
        >
          <p>{serverError.message}</p>
        </BaseAlert>
      ) : null}

      <BaseCard
        variant="surface"
        padding="lg"
      >
        <form
          className="flex flex-col gap-4"
          onSubmit={handleSearch}
          noValidate
        >
          <h2 className="text-[var(--font-h3)] font-semibold text-[var(--color-text)]">Agregar contacto</h2>
          <BaseInput
            label="Email del usuario"
            type="email"
            autoComplete="email"
            placeholder="usuario@correo.com"
            value={email}
            onChange={e => {
              setEmail(e.target.value);
              setEmailError(undefined);
              setFoundContact(null);
            }}
            error={emailError}
            disabled={searchMutation.isPending}
          />
          <div className="flex flex-col gap-2 sm:flex-row">
            <BaseButton
              type="submit"
              variant="primary"
              disabled={searchMutation.isPending}
              fullWidth
            >
              {searchMutation.isPending ? "Buscando..." : "Buscar"}
            </BaseButton>
          </div>

          {foundContact ? (
            <div className="mt-2 flex items-center justify-between gap-3 rounded-lg border border-[var(--color-border)] bg-[var(--color-bg)] p-3">
              <div className="min-w-0">
                <p className="truncate text-[var(--font-body-lg)] font-semibold text-[var(--color-text)]">
                  {foundContact.fullName}
                </p>
                <p className="truncate text-[var(--font-body)] text-[var(--color-text-tertiary)]">
                  {foundContact.email}
                </p>
              </div>
              <BaseButton
                type="button"
                variant="primary"
                size="sm"
                onClick={handleAdd}
                disabled={addMutation.isPending}
              >
                {addMutation.isPending ? "Agregando..." : "Agregar"}
              </BaseButton>
            </div>
          ) : null}
        </form>
      </BaseCard>

      <div className="flex flex-col gap-3">
        <h2 className="text-[var(--font-h2)] font-semibold text-[var(--color-text)]">Lista de contactos</h2>
        {isLoading ? (
          <BaseCard
            variant="surface"
            padding="lg"
          >
            <p className="text-center text-[var(--font-body-lg)] text-[var(--color-text-secondary)]">Cargando...</p>
          </BaseCard>
        ) : isError ? (
          <BaseAlert
            variant="danger"
            title="No pudimos cargar tus contactos"
          >
            <p>{error?.message ?? "Inténtalo nuevamente."}</p>
            <BaseButton
              variant="secondary"
              size="sm"
              onClick={() => refetch()}
            >
              Reintentar
            </BaseButton>
          </BaseAlert>
        ) : contacts.length === 0 ? (
          <BaseCard
            variant="surface"
            padding="lg"
          >
            <p className="text-center text-[var(--font-body-lg)] text-[var(--color-text-secondary)]">
              Aún no tienes contactos.
            </p>
          </BaseCard>
        ) : (
          <ul className="flex flex-col gap-3">
            {contacts.map(contact => (
              <li key={contact.id}>
                <BaseCard
                  variant="surface"
                  padding="md"
                  className="flex items-center justify-between gap-3"
                >
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-[var(--font-body-lg)] font-semibold text-[var(--color-text)]">
                      {contact.fullName}
                    </p>
                    <p className="truncate text-[var(--font-body)] text-[var(--color-text-tertiary)]">
                      {contact.email}
                    </p>
                    {contact.nickname ? (
                      <div className="mt-1">
                        <BaseBadge
                          variant="neutral"
                          size="sm"
                        >
                          {contact.nickname}
                        </BaseBadge>
                      </div>
                    ) : null}
                  </div>
                  <BaseButton
                    variant="danger"
                    size="sm"
                    onClick={() => handleDelete(contact)}
                    disabled={deleteMutation.isPending}
                  >
                    Eliminar
                  </BaseButton>
                </BaseCard>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
};

export default ContactsPage;
